import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faSave, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import { Survey } from '../../../types';
import { getSurveys, createSurvey, updateSurvey, deleteSurvey } from '../../../api/surveyApi';
import { getSortIcon, containsEmoji } from '../../../utils/Utils';
import './AdminSurveyConfig.css';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const AdminSurveyConfig: React.FC = () => {
  const [surveyQuestions, setSurveyQuestions] = useState<Array<Survey>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<string>('');
  const [editingInputType, setEditingInputType] = useState<'radio' | 'textarea'>('radio');
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newQuestion, setNewQuestion] = useState<string>('');
  const [newInputType, setNewInputType] = useState<'radio' | 'textarea'>('radio');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'order', direction: 'asc' });
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async (): Promise<void> => {
    try {
      setLoading(true);
      const surveyData = await getSurveys();
      if (surveyData && surveyData.length > 0) {
        setSurveyQuestions(surveyData);
      }
      setError('');
    } catch (error: any) {
      console.error('Error fetching surveys:', error);
      setError('アンケートの取得中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (survey: Survey): void => {
    setEditingId(survey.id);
    setEditingQuestion(survey.text || '');
    setEditingInputType(survey.input_type || 'radio');
  };

  const validateSurveyText = (text: string, excludeId?: number): string | null => {
    if (!text.trim()) {
      return 'アンケート内容を入力してください。';
    }

    if (containsEmoji(text)) {
      return '絵文字は使用できません。';
    }

    const trimmedText = text.trim();
    const isDuplicate = surveyQuestions.some(survey => {
      const surveyText = (survey.text || '').trim();
      return surveyText === trimmedText && survey.id !== excludeId;
    });

    if (isDuplicate) {
      return 'このアンケート内容は既に登録されています。';
    }

    return null;
  };

  const handleCancelEdit = (): void => {
    setEditingId(null);
    setEditingQuestion('');
    setValidationErrors({});
    setIsAdding(false);
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editingId) return;

    const validationError = validateSurveyText(editingQuestion, editingId);
    if (validationError) {
      setValidationErrors({ [`edit_${editingId}`]: validationError });
      return;
    }

    try {
      setIsSubmitting(true);
      const survey = surveyQuestions.find(s => s.id === editingId);
      if (!survey) return;

      const updatedSurvey = await updateSurvey(editingId, {
        order: survey.order,
        text: editingQuestion.trim(),
        input_type: editingInputType
      });

      setSurveyQuestions(prevSurveys => 
        prevSurveys.map(survey => 
          survey.id === editingId 
            ? { ...updatedSurvey }
            : survey
        )
      );

      setEditingId(null);
      setEditingQuestion('');
      setValidationErrors({});
    } catch (error: any) {
      console.error('Error updating survey:', error);
      if (error.response?.data?.errors?.text) {
        setValidationErrors({ [`edit_${editingId}`]: error.response.data.errors.text[0] });
      } else {
        alert('アンケートの更新が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddClick = (): void => {
    setIsAdding(true);
    setNewQuestion('');
    setEditingId(null);
  };

  const handleCancelAdd = (): void => {
    setIsAdding(false);
    setNewQuestion('');
    setValidationErrors({});
  };

  const handleSaveAdd = async (): Promise<void> => {
    const validationError = validateSurveyText(newQuestion);
    if (validationError) {
      setValidationErrors({ 'new_question': validationError });
      return;
    }

    if (surveyQuestions.length >= 10) {
      alert('アンケートは最大10件まで登録できます。');
      return;
    }

    try {
      setIsSubmitting(true);
      const nextOrder = Math.max(...surveyQuestions.map(survey => survey.order), 0) + 1;

      const newSurvey = await createSurvey({
        order: nextOrder,
        text: newQuestion.trim(),
        input_type: newInputType
      });

      setSurveyQuestions(prevSurveys => [
        ...prevSurveys,
        { ...newSurvey }
      ]);

      setIsAdding(false);
      setNewQuestion('');
      setNewInputType('radio');
      setValidationErrors({});
    } catch (error: any) {
      console.error('Error creating survey:', error);
      if (error.response?.data?.errors?.text) {
        setValidationErrors({ 'new_question': error.response.data.errors.text[0] });
      } else if (error.response?.status === 422) {
        alert(error.response.data.message || 'アンケートは最大10件まで登録できます');
      } else {
        alert('アンケートの作成が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!window.confirm(`Q${id}のアンケートを削除してもよろしいですか？`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteSurvey(id);
      
      await fetchSurveys();
    } catch (error: any) {
      console.error('Error deleting surveys:', error);
      if (error.response?.status === 422) {
        alert('アンケートは最低1件必要です');
      } else {
        alert('アンケートの削除が失敗しました。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSurveys = React.useMemo(() => {
    let filtered = surveyQuestions.filter(survey =>
      (survey.text || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.key) {
        case 'order':
          aValue = a.order;
          bValue = b.order;
          break;
        case 'question':
          aValue = (a.text || '').toLowerCase();
          bValue = (b.text || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [surveyQuestions, searchTerm, sortConfig]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>

        <div className="navigation-container">
          <AdminNavigation />
          <div className="search-container">
            <div className="search-input-container">
              <Form>
                <Form.Control
                  type="text"
                  placeholder="質問内容で検索"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                />
              </Form>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div className='reference-check-text'>
              アンケート設定
            </div>

            <div className="survey-info mb-3">
              <p className="text-muted">
                回答フォームに表示するアンケート質問を管理します。最大10件まで登録可能です。
              </p>
            </div>
          </div>

          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={handleAddClick}
            disabled={isSubmitting || surveyQuestions.length >= 10 || isAdding || editingId !== null}
            className="detail-button"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            アンケート追加
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        <div className="candidates-table-container">
          <table className="candidates-table survey-config-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('order')} className="sortable text-center" style={{ width: '80px' }}>
                  順序 {getSortIcon('order', sortConfig)}
                </th>
                <th onClick={() => handleSort('question')} className="sortable text-left">
                  質問内容 {getSortIcon('question', sortConfig)}
                </th>
                <th className="text-center" style={{ width: '120px' }}>回答種別</th>
                <th className="text-center" style={{ width: '150px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {sortedSurveys.map((survey) => (
                <tr key={survey.id}>
                  <td className="text-center">
                    <span className="question-index">Q{survey.order}.</span>
                  </td>
                  <td className="text-left" colSpan={editingId === survey.id ? 2 : 1}>
                    {editingId === survey.id ? (
                      <div className="question-input">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <Form.Control
                              type="text"
                              value={editingQuestion}
                              onChange={(e) => {
                                setEditingQuestion(e.target.value);
                                if (validationErrors[`edit_${survey.id}`]) {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`edit_${survey.id}`];
                                    return newErrors;
                                  });
                                }
                              }}
                              placeholder="アンケート内容を入力してください"
                              maxLength={500}
                              disabled={isSubmitting}
                              isInvalid={!!validationErrors[`edit_${survey.id}`]}
                            />
                            {validationErrors[`edit_${survey.id}`] && (
                              <Form.Control.Feedback type="invalid">
                                {validationErrors[`edit_${survey.id}`]}
                              </Form.Control.Feedback>
                            )}
                            <div className="mt-2 d-flex gap-3">
                              <Form.Check
                                type="radio"
                                id={`edit-textarea-${survey.id}`}
                                label="フリーテキスト"
                                checked={editingInputType === 'textarea'}
                                onChange={() => setEditingInputType('textarea')}
                                disabled={isSubmitting}
                              />
                              <Form.Check
                                type="radio"
                                id={`edit-radio-${survey.id}`}
                                label="ラジオボタン"
                                checked={editingInputType === 'radio'}
                                onChange={() => setEditingInputType('radio')}
                                disabled={isSubmitting}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="question-text mb-0">{survey.text || ''}</p>
                    )}
                  </td>
                  {editingId !== survey.id && (
                    <td className="text-center">
                      <span>{survey.input_type === 'textarea' ? 'フリーテキスト' : 'ラジオボタン'}</span>
                    </td>
                  )}
                  <td className="text-center">
                    {editingId === survey.id ? (
                      <div>
                        <Button
                          className="detail-button me-2"
                          onClick={handleSaveEdit}
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faSave} className="me-1" />
                          保存
                        </Button>
                        <Button
                          className="cancel-button-icon"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Button
                          className="detail-button me-2"
                          onClick={() => handleEditClick(survey)}
                          disabled={editingId !== null || isSubmitting || isAdding}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          編集
                        </Button>
                        <Button
                          className="delete-button-icon"
                          onClick={() => handleDelete(survey.id)}
                          disabled={editingId !== null || isSubmitting || surveyQuestions.length <= 1 || isAdding}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {isAdding && (
                <tr>
                  <td className="text-center">
                    <span className="question-index">Q{surveyQuestions.length + 1}.</span>
                  </td>
                  <td className="text-left" colSpan={2}>
                    <div className="question-input">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <Form.Control
                            type="text"
                            value={newQuestion}
                            onChange={(e) => {
                              setNewQuestion(e.target.value);
                              if (validationErrors['new_question']) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors['new_question'];
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="アンケート内容を入力してください"
                            maxLength={500}
                            disabled={isSubmitting}
                            isInvalid={!!validationErrors['new_question']}
                          />
                          {validationErrors['new_question'] && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors['new_question']}
                            </Form.Control.Feedback>
                          )}
                          <div className="mt-2 d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="new-textarea"
                              label="フリーテキスト"
                              checked={newInputType === 'textarea'}
                              onChange={() => setNewInputType('textarea')}
                              disabled={isSubmitting}
                            />
                            <Form.Check
                              type="radio"
                              id="new-radio"
                              label="ラジオボタン"
                              checked={newInputType === 'radio'}
                              onChange={() => setNewInputType('radio')}
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center">
                    <div>
                      <Button
                        className="detail-button me-2"
                        onClick={handleSaveAdd}
                        disabled={isSubmitting}
                      >
                        <FontAwesomeIcon icon={faSave} className="me-1" />
                        保存
                      </Button>
                      <Button
                        className="cancel-button-icon"
                        onClick={handleCancelAdd}
                        disabled={isSubmitting}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              {sortedSurveys.length === 0 && !isAdding && (
                <tr>
                  <td colSpan={4} className="text-center">
                    {searchTerm ? '検索条件に一致するアンケートが見つかりません。' : 'アンケートが登録されていません。'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </Container>
    </div>
  );
};

export default AdminSurveyConfig;
